import React, { Fragment, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { ModalWithIcon } from "@scripts/react/components/RfModal/ModalWithIcon";

import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { useAuth } from "@/contexts/AuthContext";
import Button from "@components/Button";
import { useFlow } from "@/contexts/FlowContext";
import { BaseWorkspaceSwitcherButton } from "@components/Navbar/BaseWorkspaceSwitcherButton";
import useApi from "@/hooks/useApi";
import {
    useWorkspaces,
    useCurrentWorkspace,
    useWorkspaceUsages,
    useSwitchWorkspace,
    useCurrentWorkflow,
    useWorkspaceLoading
} from "@/store";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsAuthenticationEvent } from "@/analytics/types";
import { User } from "firebase/auth";
import RapidLogo from "@scripts/react/components/RapidLogo";
import { DataVisibilitySwitcher } from "./DataVisibilitySwitcher";
import { CreditChip } from "./CreditPromoComponents/CreditChip";
import { useListenCurrentWorkspaceUsage } from "@/hooks/useListenCurrentWorkspaceUsage";
import { UserAvatar } from "@scripts/react/components/UserAvatar";
import { SignOutButton } from "@scripts/react/components/SignOutButton";
import { useTutorial } from "@/hooks/useTutorial";
import { projectSelector } from "@rflocal/shared";
import { BuildSteps, Flows } from "@/types/flows";
import { useFlowState } from "@/hooks/useFlowState";
import { InlineLoadingSpinner } from "@scripts/react/components/LoadingSpinner";
import { useMobile } from "@/hooks/useMobile";

// Simple navigation link that just works
const SimpleNavLink = ({
    children,
    to,
    className,
    onClick
}: {
    children: React.ReactNode;
    to: string;
    className?: string;
    onClick?: () => void;
}) => {
    const navigate = useNavigate();

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();

        if (onClick) {
            onClick();
        }

        // Just navigate without resetting flow state
        navigate(to);
    };

    return (
        <a
            href={to}
            className={twClassNames("menu-item !text-sm", className)}
            onClick={handleClick}
        >
            {children}
        </a>
    );
};

const AuthItem = ({ isMobile }: { isMobile: boolean }) => {
    const { user, requireLogin, logout } = useAuth();
    const workspace = useCurrentWorkspace();
    const { track } = useAnalytics();
    const { flow } = useFlowState();
    const workflow = useCurrentWorkflow();
    const hasWorkflow = workflow?.url && workspace?.url && flow === Flows.DEPLOY;

    // Check current route for active state
    const currentPath = window.location.pathname;
    const isModelsPage = currentPath.includes("/models");
    const isHomePage =
        currentPath === "/" || (workspace?.url && currentPath === `/${workspace.url}`);

    const handleGoToLegacy = useCallback(() => {
        if (hasWorkflow) {
            window.open(
                `${projectSelector.appUrl(false, true)}/${workspace.url}/workflows/edit/${workflow.url}`,
                "_blank"
            );
        } else {
            const workspaceUrlPath = workspace?.url ? `/${workspace.url}` : "";
            window.open(`${projectSelector.appUrl(false, true)}${workspaceUrlPath}`, "_blank");
        }
    }, [hasWorkflow, workspace?.url, workflow?.url]);

    const handleLogout = useCallback(async () => {
        try {
            // clone copy to persist metadata after logout
            // for event tracking
            const u = { ...user } as User;

            await logout();

            // Clear analytics session
            track({
                event: AnalyticsAuthenticationEvent.USER_SIGNED_OUT,
                data: { email: u.email, method: u.providerData[0]?.providerId, name: u.displayName }
            });
        } catch (error) {
            console.error("Error during logout:", error);
        }
    }, [logout, track, user]);

    if (!user) {
        return (
            <Button onClick={() => requireLogin()} type="purbo-ghost" size="small">
                Sign in
            </Button>
        );
    }

    return (
        <Menu as="div" className="headless-menu">
            {/* Avatar */}
            <Menu.Button className="flex focus:outline-none focus:ring-0">
                <UserAvatar
                    user={user}
                    className="h-full w-full cursor-pointer"
                    showAvatar
                    avatarSize="medium"
                    showTooltip={false}
                />
            </Menu.Button>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className="menu-container right-0 mt-2 !w-80 origin-top-right">
                    <div className="menu-item subtitle font-sans">
                        <UserAvatar
                            user={user}
                            className="h-full w-full cursor-pointer"
                            textClassName="!text-sm"
                            displayNameClassName="!font-semibold"
                            showAvatar
                            showEmail
                            showName
                            avatarSize="medium"
                        />
                    </div>
                    {/* Show WorkspaceSelector in dropdown on mobile */}
                    {isMobile && (
                        <Menu.Item>
                            <div className="px-2 py-1">
                                <WorkspaceSelector />
                            </div>
                        </Menu.Item>
                    )}
                    {workspace && (
                        <>
                            <Menu.Item>
                                <SimpleNavLink
                                    to={`/${workspace.url}`}
                                    className={twClassNames(
                                        isHomePage
                                            ? "bg-tint text-brand hover:bg-tint-hover"
                                            : "!text-secondary hover:bg-gray-100 hover:text-primary"
                                    )}
                                >
                                    <i
                                        className={twClassNames(
                                            "far fa-fw fa-plus-circle",
                                            isHomePage ? "!text-brand" : "text-tertiary"
                                        )}
                                    ></i>
                                    Create Model
                                </SimpleNavLink>
                            </Menu.Item>
                            <Menu.Item>
                                <SimpleNavLink
                                    to={`/${workspace.url}/models`}
                                    className={twClassNames(
                                        "!text-secondary hover:bg-gray-100 hover:text-primary"
                                    )}
                                >
                                    <i
                                        className={twClassNames(
                                            "far fa-fw fa-cube",
                                            "text-tertiary"
                                        )}
                                    ></i>
                                    My Models
                                </SimpleNavLink>
                            </Menu.Item>
                        </>
                    )}
                    <hr />
                    <Menu.Item>
                        <Button
                            type="ghost"
                            size="small"
                            className="justify-between border-none"
                            onClick={handleGoToLegacy}
                        >
                            {hasWorkflow ? "View in Roboflow Workflows" : "Go to Roboflow App"}
                            <i className="far fa-fw fa-arrow-up-right text-tertiary"></i>
                        </Button>
                    </Menu.Item>
                    <Menu.Item>
                        <SignOutButton
                            handleLogout={handleLogout}
                            size="small"
                            className="justify-between !text-destructive"
                        />
                    </Menu.Item>
                </Menu.Items>
            </Transition>
        </Menu>
    );
};

const WorkspaceSelector = () => {
    const { user } = useAuth();
    const workspaces = useWorkspaces();
    const currentWorkspace = useCurrentWorkspace();
    useListenCurrentWorkspaceUsage();
    const workspaceLoading = useWorkspaceLoading();
    const workspaceUsages = useWorkspaceUsages();
    const switchWorkspace = useSwitchWorkspace();
    const api = useApi();
    const { setStep, flow } = useFlowState();

    // Transform workspaces array to object format expected by BaseWorkspaceSwitcherButton
    const workspacesObj = useMemo(() => {
        return (
            workspaces?.reduce(
                (acc: Record<string, any>, workspace: any) => {
                    acc[workspace.id] = workspace;
                    return acc;
                },
                {} as Record<string, any>
            ) ?? {}
        );
    }, [workspaces]);

    // Transform workspaceUsages array to object format
    const workspaceUsagesObj = useMemo(() => {
        return (
            workspaceUsages?.reduce(
                (acc: Record<string, any>, usage: any) => {
                    acc[usage.id] = usage;
                    return acc;
                },
                {} as Record<string, any>
            ) ?? {}
        );
    }, [workspaceUsages]);

    // Adapt user data format
    const userForSwitcher = useMemo(() => {
        if (!user) return null;
        return {
            name: user.displayName || user.email || "",
            email: user.email || "",
            profilePhoto: user.photoURL || undefined,
            uid: user.uid
        };
    }, [user]);

    // Custom workspace selection handler that uses our Zustand store
    const handleWorkspaceSelect = useCallback(
        (workspaceId: string) => {
            const selectedWorkspace = workspaces?.find((w: any) => w.id === workspaceId);
            if (selectedWorkspace) {
                switchWorkspace(selectedWorkspace, api, () => {
                    // Always switch to build flow when switching workspaces
                    const newStep = flow === Flows.DEPLOY ? BuildSteps.UPLOAD : null;
                    setStep({
                        flow: Flows.BUILD,
                        step: newStep,
                        workspaceUrl: selectedWorkspace.url,
                        replace: false
                    });
                });
            }
        },
        [workspaces, switchWorkspace, api, flow, setStep]
    );

    // console.log("NavBar WorkspaceSelector, ", user, workspaceLoading, currentWorkspace, workspaces);

    // Don't show if user has no workspaces or only one workspace
    if (!user) {
        return null;
    } else if (workspaceLoading || !currentWorkspace || !workspaces || workspaces?.length <= 1) {
        return <InlineLoadingSpinner type={"secondary"} />;
    }

    return (
        <div className="iris-workspace-selector relative isolate z-10 flex h-full items-stretch">
            <BaseWorkspaceSwitcherButton
                user={userForSwitcher}
                currentWorkspace={currentWorkspace}
                workspaces={workspacesObj}
                workspaceUsages={workspaceUsagesObj}
                onWorkspaceSelect={handleWorkspaceSelect}
                dropdownPosition="right"
                searchThreshold={3}
                theme="light"
                hideChevron
                className="rounded-none hover:bg-gray-100 hover:bg-opacity-100 md:px-3"
                dropdownClassName="right-1 top-12 !w-80 scale-100 transform !p-0 opacity-100"
            />
        </div>
    );
};

export const Navbar = () => {
    const [showWarningModal, setShowWarningModal] = useState(false);
    const navigate = useNavigate();
    const { reset } = useFlow();
    const { open: openTutorial, showReopenHint, clearReopenHint } = useTutorial();
    const isMobile = useMobile();

    const handleTutorialClick = useCallback(() => {
        clearReopenHint();
        openTutorial();
    }, [clearReopenHint, openTutorial]);

    const handleConfirmNavigation = useCallback(() => {
        setShowWarningModal(false);
        // Always navigate to the root home page
        navigate("/");
        reset();
    }, [navigate, reset]);

    const handleHomeClick = useCallback(() => {
        const currentRoute = window.location.pathname;
        const pathSegments = currentRoute.split("/").filter(Boolean);

        // If we're already on the home page (either root or workspace root), refresh the page
        if (pathSegments.length <= 1) {
            window.location.reload();
            return;
        }

        // Only show warning modal if we're on a /build route
        if (currentRoute.includes("/build")) {
            setShowWarningModal(true);
            return;
        }

        // Otherwise, just navigate normally without resetting
        // Let the homepage handle its own initialization
        navigate("/");
    }, [navigate]);

    return (
        <>
            <nav
                className={twClassNames(
                    "duration-50 relative flex h-12 items-stretch justify-between bg-white px-5 transition-colors md:px-6"
                )}
            >
                <div className="flex items-center gap-2">
                    <a
                        href="/"
                        onClick={(e) => {
                            e.preventDefault();
                            handleHomeClick();
                        }}
                        className="group"
                    >
                        <RapidLogo />
                    </a>
                    <div className="relative mt-1">
                        <AnimatePresence>
                            {showReopenHint && (
                                <motion.span
                                    className="absolute inset-0 animate-pulse rounded-md ring-2 ring-purple-400 ring-offset-1"
                                    initial={{ opacity: 0, scale: 0.5 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.5 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 15 }}
                                />
                            )}
                        </AnimatePresence>
                        <Button
                            onClick={handleTutorialClick}
                            type="purbo-ghost"
                            shape="square"
                            size="xxsmall"
                            className="relative"
                        >
                            <i className="far fa-circle-question" />
                        </Button>
                        <AnimatePresence>
                            {showReopenHint && (
                                <motion.div
                                    className="absolute left-full top-1/2 ml-3 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs font-medium text-white shadow-md"
                                    initial={{ opacity: 0, x: -8, y: "-50%" }}
                                    animate={{ opacity: 1, x: 0, y: "-50%" }}
                                    exit={{ opacity: 0, x: -8, y: "-50%" }}
                                    transition={{ duration: 0.2, ease: "easeOut" }}
                                >
                                    Click here to reopen tutorial
                                    <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <CreditChip />
                    <div className="hidden md:block">
                        <DataVisibilitySwitcher />
                    </div>
                    {/* Hide WorkspaceSelector on mobile - it's shown in AuthItem dropdown instead */}
                    {!isMobile && <WorkspaceSelector />}
                    <AuthItem isMobile={isMobile} />
                </div>
            </nav>

            <ModalWithIcon
                isOpen={showWarningModal}
                onClose={() => setShowWarningModal(false)}
                type="warning"
                icon="fad fa-exclamation-triangle"
                title="Warning: You Will Lose All Progress"
                submitButtonText="Return to Home"
                cancelButtonText="Cancel"
                onSubmit={handleConfirmNavigation}
                onCancel={() => setShowWarningModal(false)}
            >
                Are you sure you want to return to home? Your progress will be reset.
            </ModalWithIcon>
        </>
    );
};
